import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ui/ModalCloseButton';
import ColorPaletteEditor from './ColorPaletteEditor';
import { getSavedPalette, isValidHex, normalizePalette } from '../utils/colorPalette';
import { getConceptLabelForIntent } from '../utils/themePlanKeys';

const ThemeEditModal = ({ isOpen, onClose, onSave, theme, eventData }) => {
    const [editedTitle, setEditedTitle] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [editedPalette, setEditedPalette] = useState([]);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

    useEffect(() => {
        if (theme) {
            setEditedTitle(theme.name || '');
            setEditedDescription(theme.description || '');
            setEditedPalette(normalizePalette(theme.color_palette || []));
            setPendingDeleteIndex(null);
        }
    }, [theme, isOpen]);

    if (!isOpen || !theme) return null;

    const savedCount = editedPalette.filter((color) => isValidHex(color.hex)).length;
    const isDirty = (
        editedTitle !== (theme.name || '') ||
        editedDescription !== (theme.description || '') ||
        JSON.stringify(getSavedPalette(editedPalette)) !== JSON.stringify(normalizePalette(theme.color_palette || []))
    );

    const handleSave = () => {
        onSave({
            ...theme,
            name: editedTitle,
            description: editedDescription,
            color_palette: getSavedPalette(editedPalette)
        });
        onClose();
    };

    const removePaletteColor = (index) => {
        if (savedCount <= 1) return;
        setEditedPalette((prev) => prev.filter((_, idx) => idx !== index));
        setPendingDeleteIndex(null);
    };

    const portalTarget = typeof document !== 'undefined' ? document.body : null;
    const modalContent = (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <h3 className="text-xl font-bold text-gray-900">Edit {getConceptLabelForIntent(eventData?.focus_selection, 'concept')}</h3>
                    <ModalCloseButton onClick={onClose} />
                </div>

                <div className="overflow-y-auto p-5 sm:p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            {getConceptLabelForIntent(eventData?.focus_selection, 'concept')} Title
                        </label>
                        <input
                            type="text"
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all font-semibold text-gray-900"
                            placeholder={`Give your ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')} a catchy name`}
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            Concept Description
                        </label>
                        <textarea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            rows={6}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-gray-600 leading-relaxed resize-none"
                            placeholder="Describe the vibe, atmosphere, and key elements..."
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Tip: Changing the description helps refine this idea. Use "More like this" from the {getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')} menu when you want a fresh set inspired by it.
                        </p>
                    </div>

                    <ColorPaletteEditor
                        palette={editedPalette}
                        onPaletteChange={setEditedPalette}
                        onRequestRemove={setPendingDeleteIndex}
                        minColors={1}
                    />
                </div>

                <div className="p-5 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!isDirty}
                        className={`px-6 py-2.5 rounded-xl font-bold transition-all ${
                            isDirty 
                                ? 'text-white bg-gradient-to-r from-primary to-primary-dark shadow-md hover:shadow-lg hover:-translate-y-0.5' 
                                : 'text-gray-400 bg-gray-200 cursor-not-allowed shadow-none'
                        }`}
                    >
                        Save Changes
                    </button>
                </div>

                {pendingDeleteIndex !== null && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
                            <h4 className="text-lg font-extrabold text-gray-950">Remove this color?</h4>
                            <p className="mt-2 text-sm leading-6 text-gray-600">
                                {[editedPalette[pendingDeleteIndex]?.name, editedPalette[pendingDeleteIndex]?.hex].filter(Boolean).join(' ') || 'This color'} will be removed from the ${getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')} palette.
                            </p>
                            <div className="mt-5 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setPendingDeleteIndex(null)}
                                    className="h-10 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removePaletteColor(pendingDeleteIndex)}
                                    className="h-10 rounded-xl bg-red-600 px-4 text-sm font-bold text-white transition hover:bg-red-700"
                                >
                                    Remove color
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return portalTarget ? createPortal(modalContent, portalTarget) : modalContent;
};

export default ThemeEditModal;
